import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useLang } from "@/context/LangContext";

const APP_VERSION = "4.4";
const STORAGE_KEY = "tigik_seen_version";

const VB = {
  sk: { title: "Nová verzia Tigik", click: "Klikni aktualizovať", btn: "Obnoviť", close: "Zavrieť" },
  en: { title: "New Tigik version", click: "Click to update", btn: "Refresh", close: "Close" },
  cs: { title: "Nová verze Tigik", click: "Klikni aktualizovat", btn: "Obnovit", close: "Zavřít" },
  de: { title: "Neue Tigik-Version", click: "Zum Aktualisieren klicken", btn: "Aktualisieren", close: "Schließen" },
  pl: { title: "Nowa wersja Tigik", click: "Kliknij, aby zaktualizować", btn: "Odśwież", close: "Zamknij" },
  fr: { title: "Nouvelle version Tigik", click: "Clique pour mettre à jour", btn: "Actualiser", close: "Fermer" },
};

export default function VersionBanner() {
  const [show, setShow] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const { lang } = useLang() || { lang: "sk" };
  const tr = VB[lang] || VB.sk;

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen !== APP_VERSION) {
      // New version since last visit
      setShow(true);
    }
  }, []);

  // Auto-dismiss after 5 seconds with a 400ms fade-out, then remember in localStorage
  useEffect(() => {
    if (!show) return;
    const fadeTimer = setTimeout(() => setFadingOut(true), 4600);
    const hideTimer = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
      setShow(false);
    }, 5000);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, [show]);

  const refresh = () => {
    // Bust caches and reload
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      });
    }
    if (window.caches) {
      caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
    }
    localStorage.setItem(STORAGE_KEY, APP_VERSION);
    setTimeout(() => window.location.reload(true), 200);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, APP_VERSION);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="version-banner"
      style={{
        opacity: fadingOut ? 0 : 1,
        transform: fadingOut ? "translateY(-8px)" : "translateY(0)",
        transition: "opacity 400ms ease, transform 400ms ease",
      }}
      className="fixed top-16 left-3 right-3 md:left-auto md:right-6 md:w-96 z-40 flex items-center gap-3 px-4 py-3 border border-gold-700 bg-zinc-950/95 backdrop-blur-md shadow-[0_8px_30px_-10px_rgba(212,175,55,0.6)]"
    >
      <div className="w-9 h-9 flex items-center justify-center bg-gold-500/15 border border-gold-700">
        <RefreshCw className="w-4 h-4 text-gold-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm">{tr.title} {APP_VERSION}</div>
        <div className="text-[11px] text-zinc-400 font-mono uppercase tracking-wider">{tr.click}</div>
      </div>
      <button onClick={refresh} data-testid="version-refresh-btn"
        className="wb-btn-primary px-3 py-2 text-xs">{tr.btn}</button>
      <button onClick={dismiss} aria-label={tr.close} data-testid="version-dismiss"
        className="text-zinc-500 hover:text-white p-1">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
